import Vue from 'vue';
import types from '../mutation-types';
import WhatsappCampaignsAPI from '../../api/whatsappCampaigns';

const DEFAULT_META = { count: 0, currentPage: 1, perPage: 20 };

const unwrap = data =>
  data && typeof data === 'object' && data.payload !== undefined
    ? data.payload
    : data;

const normalizeMeta = (meta = {}, fallback = DEFAULT_META) => ({
  count: Number(meta.count ?? fallback.count) || 0,
  currentPage: Number(meta.current_page ?? fallback.currentPage) || 1,
  perPage: Number(meta.per_page ?? fallback.perPage) || fallback.perPage,
});

export const state = {
  records: {},
  sortOrder: [],
  meta: { ...DEFAULT_META },
  recipients: {},
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isLaunching: false,
    isFetchingRecipients: false,
    isEstimating: false,
  },
};

export const getters = {
  getCampaigns: $state =>
    $state.sortOrder.map(id => $state.records[id]).filter(Boolean),
  getCampaign: $state => id => $state.records[id],
  getMeta: $state => $state.meta,
  getRecipients: $state => campaignId =>
    $state.recipients[campaignId]?.records || [],
  getRecipientsMeta: $state => campaignId =>
    $state.recipients[campaignId]?.meta || { ...DEFAULT_META },
  getUIFlags: $state => $state.uiFlags,
};

let latestListRequest = 0;

export const actions = {
  async get({ commit }, params = {}) {
    latestListRequest += 1;
    const requestId = latestListRequest;
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await WhatsappCampaignsAPI.get(params);
      const payload = Array.isArray(data) ? data : data?.payload || [];
      if (requestId !== latestListRequest) return null;
      commit(types.SET_WA_CAMPAIGNS, payload);
      commit(types.SET_WA_CAMPAIGNS_META, {
        ...(data?.meta || {}),
        per_page: data?.meta?.per_page ?? params.per_page,
        current_page: data?.meta?.current_page ?? params.page,
      });
      return payload;
    } finally {
      if (requestId === latestListRequest) {
        commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isFetching: false });
      }
    }
  },

  async show({ commit }, id) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await WhatsappCampaignsAPI.show(id);
      const campaign = unwrap(data);
      commit(types.SET_WA_CAMPAIGN, campaign);
      return campaign;
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isFetchingItem: false });
    }
  },

  async create({ commit }, { launch, ...payload }) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isCreating: true });
    try {
      const { data } = await WhatsappCampaignsAPI.create(payload, launch);
      const campaign = unwrap(data);
      commit(types.SET_WA_CAMPAIGN, campaign);
      return campaign;
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isCreating: false });
    }
  },

  async update({ commit }, { id, launch, ...payload }) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await WhatsappCampaignsAPI.update(id, payload, launch);
      const campaign = unwrap(data);
      commit(types.SET_WA_CAMPAIGN, campaign);
      return campaign;
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isDeleting: true });
    try {
      await WhatsappCampaignsAPI.delete(id);
      commit(types.DELETE_WA_CAMPAIGN, id);
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isDeleting: false });
    }
  },

  async launch({ commit }, { id, action }) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isLaunching: true });
    try {
      const { data } = await WhatsappCampaignsAPI.launch(id, action);
      const campaign = unwrap(data);
      commit(types.SET_WA_CAMPAIGN, campaign);
      return campaign;
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isLaunching: false });
    }
  },

  async cancel({ commit }, id) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await WhatsappCampaignsAPI.cancel(id);
      const campaign = unwrap(data);
      commit(types.SET_WA_CAMPAIGN, campaign);
      return campaign;
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isUpdating: false });
    }
  },

  async duplicate({ commit }, id) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isCreating: true });
    try {
      const { data } = await WhatsappCampaignsAPI.duplicate(id);
      const campaign = unwrap(data);
      commit(types.SET_WA_CAMPAIGN, campaign);
      return campaign;
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isCreating: false });
    }
  },

  async getRecipients({ commit }, { campaignId, ...params }) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isFetchingRecipients: true });
    try {
      const { data } = await WhatsappCampaignsAPI.getRecipients(
        campaignId,
        params
      );
      const records = Array.isArray(data) ? data : data?.payload || [];
      commit(types.SET_WA_CAMPAIGN_RECIPIENTS, {
        campaignId,
        records,
        meta: {
          ...(data?.meta || {}),
          per_page: data?.meta?.per_page ?? params.per_page,
          current_page: data?.meta?.current_page ?? params.page,
        },
      });
      return records;
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isFetchingRecipients: false });
    }
  },

  async estimateReach({ commit }, { list_ids, recipient_type }) {
    commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isEstimating: true });
    try {
      const { data } = await WhatsappCampaignsAPI.estimateReach({
        list_ids,
        recipient_type,
      });
      return unwrap(data);
    } finally {
      commit(types.SET_WA_CAMPAIGNS_UI_FLAG, { isEstimating: false });
    }
  },
};

export const mutations = {
  [types.SET_WA_CAMPAIGNS_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  [types.SET_WA_CAMPAIGNS]($state, data) {
    const records = {};
    const sortOrder = (data || []).map(campaign => {
      records[campaign.id] = campaign;
      return campaign.id;
    });
    Vue.set($state, 'records', records);
    Vue.set($state, 'sortOrder', sortOrder);
  },

  [types.SET_WA_CAMPAIGNS_META]($state, data) {
    $state.meta = normalizeMeta(data, $state.meta);
  },

  [types.SET_WA_CAMPAIGN]($state, campaign) {
    if (!campaign || campaign.id === undefined) return;
    Vue.set($state.records, campaign.id, campaign);
    if (!$state.sortOrder.includes(campaign.id)) {
      $state.sortOrder = [campaign.id, ...$state.sortOrder];
    }
  },

  [types.DELETE_WA_CAMPAIGN]($state, id) {
    Vue.delete($state.records, id);
    $state.sortOrder = $state.sortOrder.filter(item => item !== id);
    Vue.delete($state.recipients, id);
  },

  [types.SET_WA_CAMPAIGN_RECIPIENTS]($state, { campaignId, records, meta }) {
    const previous = $state.recipients[campaignId]?.meta || DEFAULT_META;
    Vue.set($state.recipients, campaignId, {
      records: records || [],
      meta: normalizeMeta(meta, previous),
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
